import React from "react"
import { observer } from "mobx-react-lite"
import clsx from "clsx"

import { useController, useStore } from "@store"
import Icon from "@components/ui/Icon/Icon"
import Skeleton from "@components/ui/Skeleton/Skeleton"
import useMediaQuery from "@components/hooks/useMediaQuery"
import { AppMediaQueries } from "@framework/constants/app"
import CollapsibleContextProvider from "@components/ui/Collapsable/CollapsibleContext"
import SearchSummaryBlockStore from "@store/search/search-summary-block.store"

import InfoCard from "./InfoCard"
import LineItem from "./LineItem"
import Question from "./Question"
import { getCompanyOptions, getProductOptions } from "./SolutionOptions"
import ReportSuggestionCard from "./ReportSuggestionCard"
import PrimaryReportSuggestionCard from "./PrimaryReportSuggestionCard"

import styles from "./SidePanel.module.sass"

export type SidePanelProps = {
  isSearching: any
}

const SidePanel: React.FC<SidePanelProps> = observer(({ isSearching }) => {
  const isSmallScreen = useMediaQuery(AppMediaQueries.maxDesktop)

  const {
    restrictionsStore: access,
    factFinderSolutionStore: {
      searchFlowStore,
      highlights,
      loadingHighlights,
      workbookHighlights,
    },
  } = useStore()

  const { factFinderSolutionController } = useController()

  const summaryBlock = React.useMemo(() => {
    const data = searchFlowStore.getLatestSearchBlock()

    if (data == null) return null

    const block = data.blocks.find(
      (it) => it instanceof SearchSummaryBlockStore
    ) as SearchSummaryBlockStore | undefined

    return block ?? null
  }, [searchFlowStore.flowSequence.length, isSearching])

  const suggestedQuestions =
    summaryBlock?.searchSummary?.suggestedQuestion ?? []

  const fetchProductHighlights = (summaryBlock: SearchSummaryBlockStore) => {
    const rawAnswer = summaryBlock.searchSummary.summary || ""
    if (!rawAnswer) return

    factFinderSolutionController.getQueryHighlights(
      rawAnswer,
      searchFlowStore.questionId
    )
  }

  const fetchWorkbookHighlights = (summaryBlock: SearchSummaryBlockStore) => {
    const rawAnswer = summaryBlock.searchSummary.summary || ""

    factFinderSolutionController.loadWorkbookHighlights(rawAnswer?.trim())
  }

  React.useEffect(() => {
    if (isSearching || summaryBlock == null) return

    if (access.showSearchCompanies || access.showSearchProducts) {
      fetchProductHighlights(summaryBlock)
    }

    if (access.canSeeSearchWorkbookSuggestions) {
      fetchWorkbookHighlights(summaryBlock)
    }
  }, [isSearching, summaryBlock])

  const companiesAvailable =
    !!highlights?.companies?.length && access.showSearchCompanies

  const productsAvailable =
    !!highlights?.products?.length && access.showSearchProducts

  const questionsAvailable = !!suggestedQuestions?.length

  const suggestionsAvailable = questionsAvailable

  const reportsAvailable =
    !!workbookHighlights.total &&
    suggestionsAvailable &&
    access.canSeeSearchWorkbookSuggestions

  return (
    <CollapsibleContextProvider singleActive>
      <div
        className={clsx(styles.root, {
          [styles.visible]:
            companiesAvailable ||
            productsAvailable ||
            questionsAvailable ||
            reportsAvailable,
          [styles.moveRight]: !isSmallScreen,
        })}
      >
        {questionsAvailable && (
          <InfoCard
            icon={<Icon name="question-mark" />}
            heading="Suggested Questions"
          >
            <div className={styles.lineItemWrapper}>
              {suggestedQuestions.map((question) => (
                <Question question={question} disabled={isSearching} />
              ))}
            </div>
          </InfoCard>
        )}

        {reportsAvailable && (
          <InfoCard icon={<Icon name="line-chart" />} heading="Sales Workflows">
            <div className={styles.lineItemWrapper}>
              {workbookHighlights.data.map((report, idx) => {
                const handleClick = () => searchFlowStore.generateReport(report)

                if (idx === 0) {
                  return (
                    <PrimaryReportSuggestionCard
                      option={report}
                      onClick={handleClick}
                      disabled={isSearching}
                      key={report.type}
                    />
                  )
                }

                return (
                  <ReportSuggestionCard
                    option={report}
                    onClick={handleClick}
                    disabled={isSearching}
                    key={report.type}
                  />
                )
              })}
              {workbookHighlights.isLoading && (
                <Skeleton
                  lineHeight={30}
                  minWidth={100}
                  rounded
                  className={styles.skeletonLoader}
                />
              )}
            </div>
          </InfoCard>
        )}

        {productsAvailable && (
          <InfoCard icon={<Icon name="parcel" />} heading="Products">
            <div className={styles.lineItemWrapper}>
              {highlights?.products?.map((product) => (
                <LineItem
                  key={product.id}
                  groupId={product.id}
                  name={product.name}
                  product={product}
                  options={getProductOptions(product)}
                />
              ))}
              {loadingHighlights && (
                <Skeleton
                  lineHeight={30}
                  minWidth={100}
                  rounded
                  className={styles.skeletonLoader}
                />
              )}
            </div>
          </InfoCard>
        )}

        {companiesAvailable && (
          <InfoCard icon={<Icon name="factory" />} heading="Accounts">
            <div className={styles.lineItemWrapper}>
              {highlights?.companies?.map((company) => (
                <LineItem
                  key={company.id}
                  name={company.name}
                  groupId={company.id}
                  options={getCompanyOptions(company)}
                  info={company.url}
                />
              ))}
              {loadingHighlights && (
                <Skeleton
                  lineHeight={30}
                  minWidth={100}
                  rounded
                  className={styles.skeletonLoader}
                />
              )}
            </div>
          </InfoCard>
        )}
      </div>
    </CollapsibleContextProvider>
  )
})

export default SidePanel
